export interface PlayerSettings {
  sound: boolean;
  haptics: boolean;
}

const SETTINGS_KEY = 'rsv2.settings.v1';
export const ONBOARDING_KEY = 'rsv2.onboarding.v1';
export const DEFAULT_SETTINGS: PlayerSettings = { sound: true, haptics: true };

export function loadSettings(): PlayerSettings {
  try {
    return { ...DEFAULT_SETTINGS, ...JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? '{}') };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(settings: PlayerSettings): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

export function onboardingComplete(): boolean {
  return localStorage.getItem(ONBOARDING_KEY) === 'complete';
}

export function setOnboardingComplete(complete: boolean): void {
  if (complete) localStorage.setItem(ONBOARDING_KEY, 'complete');
  else localStorage.removeItem(ONBOARDING_KEY);
}

let audioContext: AudioContext | null = null;

async function nativeHaptic(kind: 'tap' | 'purchase' | 'level' | 'milestone' | 'ascension'): Promise<void> {
  if (kind === 'tap') await Haptics.impact({ style: ImpactStyle.Light });
  else if (kind === 'purchase') await Haptics.impact({ style: ImpactStyle.Medium });
  else if (kind === 'ascension') {
    await Haptics.notification({ type: NotificationType.Success });
    setTimeout(() => void Haptics.impact({ style: ImpactStyle.Heavy }).catch(() => undefined), 130);
  } else await Haptics.notification({ type: NotificationType.Success });
}

export function playerFeedback(settings: PlayerSettings, kind: 'tap' | 'purchase' | 'level' | 'milestone' | 'ascension'): void {
  if (settings.haptics && Capacitor.isNativePlatform()) {
    void nativeHaptic(kind).catch(() => undefined);
  } else if (settings.haptics && 'vibrate' in navigator) {
    const pattern = kind === 'ascension' ? [80, 45, 120] : kind === 'milestone' || kind === 'level' ? [45, 25, 65] : kind === 'purchase' ? 35 : 12;
    navigator.vibrate(pattern);
  }
  if (!settings.sound) return;
  try {
    audioContext ??= new AudioContext();
    const oscillator = audioContext.createOscillator();
    const gain = audioContext.createGain();
    const frequencies = { tap: 360, purchase: 520, level: 660, milestone: 780, ascension: 440 };
    oscillator.frequency.setValueAtTime(frequencies[kind], audioContext.currentTime);
    if (kind === 'ascension') oscillator.frequency.exponentialRampToValueAtTime(880, audioContext.currentTime + 0.32);
    gain.gain.setValueAtTime(0.0001, audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.08, audioContext.currentTime + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + (kind === 'ascension' ? 0.4 : 0.16));
    oscillator.connect(gain).connect(audioContext.destination);
    oscillator.start();
    oscillator.stop(audioContext.currentTime + (kind === 'ascension' ? 0.42 : 0.18));
  } catch { /* Browsers may defer audio until another user gesture. */ }
}
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
